import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { opsFetch } from "../lib/api";

type Overview = {
  ok: boolean;
  data: {
    waitlist_total: number;
    waitlist_7d: number;
    posts_published: number;
    posts_draft: number;
    growth_events_7d: number;
    growth_intents_7d: number;
    pv_7d: number;
    uv_7d: number;
    clicks_7d: number;
    conversion_7d: number;
    estimated_cny_7d: number;
    destinations_published: number;
    destinations_draft: number;
    experiences_published: number;
    experiences_draft: number;
    merchants_active: number;
    listings_published: number;
    listings_draft: number;
    listings_experience: number;
    listings_food: number;
    listings_sight: number;
    listings_blocked: number;
    merchants_unset_rate: number;
    ugc_pending: number;
    affiliate: {
      cities: number;
      hotels: number;
      klook: number;
      hubs: number;
    };
  };
};

type Tone = "violet" | "orange" | "pink" | "green";

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg aria-hidden="true" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" viewBox="0 0 24 24">
      {children}
    </svg>
  );
}

const ICONS = {
  eye: (
    <Glyph>
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.5" />
    </Glyph>
  ),
  users: (
    <Glyph>
      <path d="M16 19v-1.2A3.8 3.8 0 0 0 12.2 14H7.8A3.8 3.8 0 0 0 4 17.8V19" />
      <circle cx="10" cy="8" r="3" />
      <path d="M20 19v-1.1A3.2 3.2 0 0 0 17.2 15" />
      <path d="M16.2 5.2a2.6 2.6 0 0 1 0 4.6" />
    </Glyph>
  ),
  click: (
    <Glyph>
      <path d="M8 4v8" />
      <path d="m8 12 6.5 6.5" />
      <path d="M8 12h7.2a1.8 1.8 0 0 0 1.3-3.1L8 4" />
    </Glyph>
  ),
  funnel: (
    <Glyph>
      <path d="M4 5h16l-6 7v5l-4 2v-7L4 5Z" />
    </Glyph>
  ),
  store: (
    <Glyph>
      <path d="M4 10h16v9H4z" />
      <path d="M4 10 6 5h12l2 5" />
      <path d="M10 19v-5h4v5" />
    </Glyph>
  ),
  book: (
    <Glyph>
      <path d="M5 5.5A2.5 2.5 0 0 1 7.5 3H19v16H7.5A2.5 2.5 0 0 0 5 21.5Z" />
      <path d="M5 5.5A2.5 2.5 0 0 1 7.5 8H19" />
    </Glyph>
  ),
  spark: (
    <Glyph>
      <path d="M12 3v4" />
      <path d="M12 17v4" />
      <path d="M3 12h4" />
      <path d="M17 12h4" />
      <path d="m6 6 2.4 2.4" />
      <path d="m15.6 15.6 2.4 2.4" />
      <path d="m18 6-2.4 2.4" />
      <path d="m8.4 15.6-2.4 2.4" />
    </Glyph>
  ),
  coin: (
    <Glyph>
      <ellipse cx="12" cy="6.5" rx="7" ry="2.5" />
      <path d="M5 6.5v5c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-5" />
      <path d="M5 11.5v5c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-5" />
    </Glyph>
  ),
};

function num(value: number) {
  return new Intl.NumberFormat("en").format(value);
}

function pct(value: number) {
  return `${(value * 100).toFixed(1)}%`;
}

function Hero({
  tone,
  icon,
  label,
  value,
  hint,
}: {
  tone: Tone;
  icon: ReactNode;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <article className={`ops-hero__card ops-hero__card--${tone}`}>
      <span className="ops-hero__icon">{icon}</span>
      <p className="ops-hero__label">{label}</p>
      <p className="ops-hero__value">{value}</p>
      <p className="ops-hero__hint">{hint}</p>
    </article>
  );
}

function Stat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="ops-stat">
      <span className="ops-stat__icon">{icon}</span>
      <div>
        <p className="ops-stat__label">{label}</p>
        <p className="ops-stat__value">{value}</p>
      </div>
    </div>
  );
}

export function OverviewPage() {
  const q = useQuery({
    queryKey: ["ops-overview"],
    queryFn: () => opsFetch<Overview>("/api/ops/overview"),
  });

  if (q.isLoading) return <p className="ops-muted">Loading overview…</p>;
  if (q.isError) return <p className="ops-error">{(q.error as Error).message}</p>;
  const d = q.data!.data;
  const attention = [
    d.merchants_unset_rate > 0
      ? {
          title: d.merchants_unset_rate === 1 ? "1 active merchant has no rate" : `${d.merchants_unset_rate} active merchants have no rate`,
          body: "Clicks are counted, but estimated commission stays at ¥0 until CPA or CPS is set.",
        }
      : null,
    d.listings_blocked > 0
      ? {
          title: `${d.listings_blocked} listing${d.listings_blocked === 1 ? "" : "s"} cannot sit on the public shelf`,
          body: "Publish needs a book link and an image. Food and sights stay in ops until a later round.",
        }
      : null,
    d.ugc_pending > 0
      ? {
          title: `${d.ugc_pending} community item${d.ugc_pending === 1 ? "" : "s"} waiting`,
          body: "Questions and posts stay hidden until someone approves or rejects them.",
        }
      : null,
    d.posts_draft > 0
      ? {
          title: `${d.posts_draft} draft${d.posts_draft === 1 ? "" : "s"} not published`,
          body: "Drafts do not appear on the site. Open one only if the card is ready.",
        }
      : null,
  ].filter((item): item is { title: string; body: string } => item !== null);

  return (
    <>
      {attention.length > 0 ? (
        <div className="ops-attention">
          {attention.map((item) => (
            <article key={item.title}>
              <h2>{item.title}</h2>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      ) : (
        <p className="ops-callout ops-callout--ok">Nothing is blocked. Rates, the shelf, and the review queue are clear.</p>
      )}
      <div className="ops-hero">
        <Hero hint="Last 7 days" icon={ICONS.eye} label="Page views" tone="violet" value={num(d.pv_7d)} />
        <Hero hint="Unique sessions" icon={ICONS.users} label="Visitors" tone="pink" value={num(d.uv_7d)} />
        <Hero hint="Outbound clicks" icon={ICONS.click} label="Clicks" tone="orange" value={num(d.clicks_7d)} />
        <Hero hint="Clicks / visitors" icon={ICONS.funnel} label="Conversion" tone="green" value={pct(d.conversion_7d)} />
      </div>
      <div className="ops-mods">
        <section className="ops-mod">
          <header className="ops-mod__head">
            <span className="ops-mod__mark ops-mod__mark--violet">{ICONS.store}</span>
            <div>
              <h2>Supply</h2>
              <p>Merchants on the shelf</p>
            </div>
          </header>
          <div className="ops-mod__grid">
            <Stat icon={ICONS.store} label="Active merchants" value={num(d.merchants_active)} />
            <Stat icon={ICONS.click} label="Live listings" value={num(d.listings_published)} />
            <Stat icon={ICONS.spark} label="Experiences" value={num(d.listings_experience)} />
            <Stat icon={ICONS.book} label="Food / sights" value={`${num(d.listings_food)} / ${num(d.listings_sight)}`} />
          </div>
        </section>
        <section className="ops-mod">
          <header className="ops-mod__head">
            <span className="ops-mod__mark ops-mod__mark--pink">{ICONS.book}</span>
            <div>
              <h2>Content</h2>
              <p>Guides, cities, and reviews</p>
            </div>
          </header>
          <div className="ops-mod__grid">
            <Stat icon={ICONS.book} label="Published posts" value={num(d.posts_published)} />
            <Stat icon={ICONS.spark} label="Drafts" value={num(d.posts_draft)} />
            <Stat icon={ICONS.eye} label="Cities live" value={num(d.destinations_published)} />
            <Stat icon={ICONS.users} label="Pending reviews" value={num(d.ugc_pending)} />
          </div>
        </section>
        <section className="ops-mod">
          <header className="ops-mod__head">
            <span className="ops-mod__mark ops-mod__mark--orange">{ICONS.spark}</span>
            <div>
              <h2>Demand</h2>
              <p>Waitlist and on-site intent</p>
            </div>
          </header>
          <div className="ops-mod__grid">
            <Stat icon={ICONS.users} label="Waitlist" value={num(d.waitlist_total)} />
            <Stat icon={ICONS.spark} label="New this week" value={num(d.waitlist_7d)} />
            <Stat icon={ICONS.eye} label="Events" value={num(d.growth_events_7d)} />
            <Stat icon={ICONS.funnel} label="Intents" value={num(d.growth_intents_7d)} />
          </div>
        </section>
        <section className="ops-mod">
          <header className="ops-mod__head">
            <span className="ops-mod__mark ops-mod__mark--green">{ICONS.coin}</span>
            <div>
              <h2>Commission</h2>
              <p>Estimated from clicks, not settled</p>
            </div>
          </header>
          <div className="ops-mod__grid">
            <Stat icon={ICONS.coin} label="Estimated CPA" value={`¥${num(d.estimated_cny_7d)}`} />
            <Stat icon={ICONS.click} label="Click events" value={num(d.clicks_7d)} />
            <Stat icon={ICONS.store} label="Hotels" value={num(d.affiliate.hotels)} />
            <Stat icon={ICONS.book} label="City hubs" value={num(d.affiliate.hubs)} />
          </div>
        </section>
      </div>
    </>
  );
}
